package com.partejar.app.feature.entitlements.data.remote

import com.partejar.app.core.network.ApiConfig
import com.partejar.app.core.network.commercialConstraintOrNull
import com.partejar.app.core.session.CurrentWorkspaceRepository
import com.partejar.app.feature.entitlements.data.CommercialConstraintException
import com.partejar.app.feature.entitlements.data.EntitlementRepository
import com.partejar.app.feature.entitlements.data.PersonalEntitlement
import com.partejar.app.feature.entitlements.data.PersonalPlan
import com.partejar.app.feature.entitlements.data.WorkspaceEntitlement
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.request.post
import io.ktor.client.statement.HttpResponse
import io.ktor.http.HttpStatusCode
import kotlinx.serialization.Serializable

@Serializable
private data class PersonalEntitlementDto(
    val possuiPlano: Boolean,
    val plano: PersonalPlanDto? = null,
    val workspacesPropriosUsados: Long,
    val podeCriarWorkspace: Boolean
)

@Serializable
private data class PersonalPlanDto(
    val codigo: String,
    val gratuito: Boolean,
    val limiteWorkspacesProprios: Int? = null,
    val limiteMembrosWorkspace: Int? = null,
    val limitePacientesWorkspace: Int? = null,
    val relatorioAnualHabilitado: Boolean
)

@Serializable
private data class WorkspaceEntitlementDto(
    val plano: String,
    val usuarioAtualEhProprietario: Boolean,
    val membrosUsados: Long,
    val limiteMembros: Int? = null,
    val pacientesUsados: Long,
    val limitePacientes: Int? = null,
    val relatorioAnualHabilitado: Boolean
)

class EntitlementRemoteDataSource(
    private val client: HttpClient,
    private val apiConfig: ApiConfig,
    private val currentWorkspaceRepository: CurrentWorkspaceRepository
) : EntitlementRepository {
    override suspend fun personal(): PersonalEntitlement = personalRequest("/assinaturas/me")

    override suspend fun activateFree(): PersonalEntitlement = personalRequest("/assinaturas/free", post = true)

    override suspend fun currentWorkspace(): WorkspaceEntitlement {
        val workspaceId = currentWorkspaceRepository.currentWorkspaceId()
            ?: throw IllegalStateException("Workspace não selecionado")
        val response = client.get(apiConfig.endpoint("/assinaturas/workspace-atual")) {
            header("X-Workspace-Id", workspaceId)
        }
        if (response.status != HttpStatusCode.OK) throw response.commercialOrGeneric()
        return response.body<WorkspaceEntitlementDto>().toModel()
    }

    private suspend fun personalRequest(path: String, post: Boolean = false): PersonalEntitlement {
        val response = if (post) client.post(apiConfig.endpoint(path)) else client.get(apiConfig.endpoint(path))
        if (response.status != HttpStatusCode.OK) throw response.commercialOrGeneric()
        return response.body<PersonalEntitlementDto>().toModel()
    }

    private suspend fun HttpResponse.commercialOrGeneric(): Throwable {
        return commercialConstraintOrNull() ?: IllegalStateException("Falha comercial (${status.value})")
    }
}

private fun PersonalEntitlementDto.toModel() = PersonalEntitlement(
    hasPlan = possuiPlano,
    plan = plano?.let {
        PersonalPlan(it.codigo, it.gratuito, it.limiteWorkspacesProprios, it.limiteMembrosWorkspace, it.limitePacientesWorkspace, it.relatorioAnualHabilitado)
    },
    ownedWorkspacesUsed = workspacesPropriosUsados,
    canCreateWorkspace = podeCriarWorkspace
)

private fun WorkspaceEntitlementDto.toModel() = WorkspaceEntitlement(
    planCode = plano,
    isCurrentUserOwner = usuarioAtualEhProprietario,
    membersUsed = membrosUsados,
    memberLimit = limiteMembros,
    patientsUsed = pacientesUsados,
    patientLimit = limitePacientes,
    annualReportEnabled = relatorioAnualHabilitado
)
